// Live listener for an event's zones. Returns the zones + the underlying
// snapshot metadata so callers can show an "offline" badge and (critically)
// the operator can pause bookings while fromCache is true.

import { useEffect, useState } from "react";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import type { WithId, ZoneDoc } from "../types";

export interface ZonesState {
  zones: WithId<ZoneDoc>[];
  loaded: boolean;
  fromCache: boolean;     // true while offline / not yet ack'd by server
  hasPendingWrites: boolean;
  error: string | null;
}

export function useZones(eventId: string | null | undefined): ZonesState {
  const [state, setState] = useState<ZonesState>({
    zones: [],
    loaded: false,
    fromCache: false,
    hasPendingWrites: false,
    error: null,
  });

  useEffect(() => {
    if (!eventId) return;
    setState((s) => ({ ...s, loaded: false }));
    // Order by label so the operator + display see a stable, predictable layout.
    const q = query(
      collection(db, "events", eventId, "zones"),
      orderBy("label", "asc")
    );
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        const zones = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as ZoneDoc),
        }));
        setState({
          zones,
          loaded: true,
          fromCache: snap.metadata.fromCache,
          hasPendingWrites: snap.metadata.hasPendingWrites,
          error: null,
        });
      },
      (err) => {
        setState((s) => ({ ...s, error: err.message, loaded: true }));
      }
    );
    return () => unsub();
  }, [eventId]);

  return state;
}
